export function isInExtension(): boolean {
    return window.chrome && chrome.runtime && chrome.runtime.id ? true : false;
}

/**
 * Convert filename to .jpg extension
 * @param {string} filename
 * @returns {string} filename with .jpg extension
 */
export function filenameToJpg(filename: string): string {
    return filename.replace(/\.[^/.]+$/, '.jpg');
}

/**
 * Convert filename to given extension
 * @param {string} filename
 * @param {string} format Filename extension
 * @returns {string} filename with given extension
 */
export function filenameToFormat(filename: string, format: string): string {
    return filename.replace(/\.[^/.]+$/, `.${format}`);
}

/**
 * Converts uploaded file to base64 data url
 * @param blob
 * @returns
 */
export async function blobToDataURL(blob: any): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = (e: any) => resolve(e.target.result);
        reader.readAsDataURL(blob);
    });
}

// https://stackoverflow.com/a/12300351
export function dataURItoBlob(dataURI: string): Blob {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    let byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    let mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    let ab = new ArrayBuffer(byteString.length);

    // create a view into the buffer
    let ia = new Uint8Array(ab);

    // set the bytes of the buffer to the correct values
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    // write the ArrayBuffer to a blob, and you're done
    let blob = new Blob([ab], { type: mimeString });
    return blob;
}

/**
 * Env
 */
export function isDevelopment() {
    return import.meta.env.VITE_MY_ENVIRONMENT === 'development';
}

/**
 * Open URL in new tab
 * @param {string} url
 * @returns void
 */
export function openUrl(url: string) {
    if (isInExtension()) {
        chrome.tabs.create({
            active: true,
            url: url,
        });
        return;
    }
    window.open(url, '_blank');
}

/**
 * Opens help page
 * @returns {void}
 */
export function openHelpPage() {
    const url = getWebsiteUrl('/how-to/');
    window.open(url, '_blank');
}

/**
 * @returns {string}
 */
export function getCustomerPortalUrl() {
    const url =
        import.meta.env.VITE_MANAGE_SUBSCRIPTION_URL +
        (isDevelopment() ? '?test_mode=1' : '');
    return url;
}

/**
 * Opens customer portal
 * @returns {void}
 */
export function openCustomerPortal() {
    window.open(getCustomerPortalUrl(), '_blank');
}

/**
 * Get full website URL from an URI
 * @param {string} uri
 * @returns {string}
 */
export function getWebsiteUrl(uri: string = ''): string {
    const baseUrl = import.meta.env.VITE_WEBSITE_URL.replace(/\/$/, '');
    uri = uri.replace(/^\//, '');
    return `${baseUrl}/${uri}`;
}

export function getExtensionUrl(): string {
    const extensionId = import.meta.env.VITE_EXTENSION_ID;
    return `https://chromewebstore.google.com/detail/${extensionId}`;
}

export function getExtensionReviewsUrl(withGuide: boolean = false): string {
    const extensionId = import.meta.env.VITE_EXTENSION_ID;
    let url = `https://chrome.google.com/webstore/detail/${extensionId}/reviews`;
    if (withGuide) {
        url += `#:~:text=${encodeURIComponent('Write a review')}`;
    }
    return url;
}

export function getFeedbackFormUrl(): string {
    return import.meta.env.VITE_FEEDBACK_FORM_URL;
}

export function getUninstallFormUrl(): string {
    return import.meta.env.VITE_UNINSTALL_FORM_URL;
}

/**
 * Get support email
 * @returns {string}
 */
export function getSupportEmail() {
    return import.meta.env.VITE_SUPPORT_EMAIL as string;
}

/**
 * Get short file name, e.g.
 * 53697-long-name-of-this-image-[...].jpg
 * from
 * 53697-long-name-of-this-image-is-something-that-nobody-likes.jpg
 *
 * @param {string} fileName
 * @returns
 */
export function getShortFileName(fileName: string) {
    const maxLength = 20;
    let shortName = fileName;
    let baseName = fileName;
    // e.g. ".jpg"
    let extension = '';
    if (/\.[^.]+$/.test(fileName)) {
        baseName = fileName.replace(/\.[^.]+$/, '');
        const match = fileName.match(/\.[^.]+$/);
        if (match) {
            extension = match[0] ?? '';
        }
    }
    if (baseName.length + extension.length > maxLength) {
        shortName = baseName.substring(0, maxLength) + '[...]' + extension;
    }
    return shortName;
}

export function detectLanguageFromBrowser(): string {
    const lang =
        window.navigator.language || (window.navigator as any).userLanguage;
    return lang.split('-')[0];

    // Saving this here for later, maybe

    // or from the accept-language HTTP header
    // localeDetected = window.navigator.languages[0].split('-')[0]

    // accept-language example:
    // en-US,en;q=0.9,ru-RU;q=0.8,ru;q=0.7
}

/**
 * @param {*} fn
 * @param {*} delay
 * @return function
 */
export function debounce(fn: Function, delay: number = 300) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return function (this: any, ...args: any[]) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
}
